import { useEffect, useState } from 'react';
import {
  Box, Paper, Stepper, Step, StepLabel, Button, Typography,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel,
  Autocomplete, TextField, Alert,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import {
  enableSecurityHub, listStandards, getSecurityHubConfig, getConfigStatus,
  listSecurityHubAccounts,
} from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { POLL_INTERVAL_MS } from './pollInterval';
import OperationProgress from './OperationProgress';
import ConfigSetupDialog from './ConfigSetupDialog';
import { missingEnableCells } from './configStatus';
import MultiSelectField from './MultiSelectField';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, insetPanelSx, actionRowSx,
} from '../portalStyles';

const steps = ['Admin account', 'Regions', 'Standards', 'Members', 'Review'];

const alertSx = {
  borderRadius: '8px',
  fontFamily: FONT_BODY,
  '& .MuiAlert-message': { fontFamily: FONT_BODY },
};

const helpTextSx = {
  fontFamily: FONT_BODY,
  fontSize: '0.875rem',
  color: CK.body,
};

// SummaryRow renders one aligned key/value line of the review panel.
function SummaryRow({ label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        flexWrap: 'wrap',
        py: 0.75,
      }}
    >
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          minWidth: 0,
          wordBreak: 'break-word',
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function SecurityHubSetup({ onComplete, priorFailure }) {
  const [active, setActive] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [standards, setStandards] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // account/region pairs still missing a running recorder after enrollment; drives
  // the one-click "Enable AWS Config" prompt (ConfigSetupDialog) at handoff.
  const [configCells, setConfigCells] = useState([]);

  // Form state
  const [adminAccountId, setAdminAccountId] = useState('');
  const [homeRegion, setHomeRegion] = useState('us-east-1');
  const [linkingRegions, setLinkingRegions] = useState(['us-east-1']);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [memberAccountIds, setMemberAccountIds] = useState([]);

  // In-progress state (after submit)
  const [config, setConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const acctRes = await listSecurityHubAccounts();
        setAccounts(acctRes?.data?.accounts || acctRes?.data || []);
        const stdRes = await listStandards();
        const std = stdRes?.data || [];
        setStandards(std);
        setSelectedStandards(std.map((s) => s.arn));
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  // Poll while in_progress.
  useEffect(() => {
    if (!config || config.status !== 'in_progress') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await getSecurityHubConfig();
        const next = res?.data;
        if (next) setConfig(next);
        if (next && next.status !== 'in_progress') {
          clearInterval(id);
          // Standards won't fully evaluate without AWS Config recording in
          // every enrolled account/region. Check the config snapshot; if any
          // cell is missing a running recorder, surface a non-blocking prompt
          // before handing off via onComplete.
          //
          // Through the SHARED helper, not an inline status test. The inline one
          // this replaced looked only for 'none'/'disabled', so an account whose
          // recorder FAILED to provision was silently left out of the prompt —
          // and this is the one surface that could have fixed it at the point of
          // setup, while the Compliance Dashboard, Enrollment and Settings pages
          // all flagged it afterwards. missingEnableCells also derives cells from
          // every account x every linking region, so a region whose snapshot row
          // has not appeared yet counts as missing instead of vanishing.
          //
          // linkingRegions is this wizard's own selection, which is exactly the
          // set just submitted for enrollment.
          const cs = await getConfigStatus().catch(() => null);
          const missing = missingEnableCells(cs?.data?.recorders || [], linkingRegions);
          if (missing.length > 0) {
            setConfigCells(missing);
            return;
          }
          if (onComplete) onComplete();
        }
      } catch (_) { /* swallow transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [config, onComplete, linkingRegions]);

  const next = () => setActive((s) => s + 1);
  const back = () => setActive((s) => s - 1);

  const canNext = () => {
    if (active === 0) return !!adminAccountId;
    if (active === 1) return !!homeRegion && linkingRegions.length > 0 && linkingRegions.includes(homeRegion);
    if (active === 2) return selectedStandards.length > 0;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await enableSecurityHub({
        adminAccountId,
        homeRegion,
        linkingRegions,
        enabledStandards: selectedStandards,
        memberAccountIds,
      });
      setConfig(res?.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Step bodies carry no extra padding: the card already pads 24px, so the
  // content lines up with the stepper above and the action row below it.
  const body = () => {
    if (config) {
      const statusLabel = {
        in_progress: 'Setting up Security Hub…',
        enabled: 'Security Hub is enabled',
        partial_failure: 'Security Hub enabled with partial failures',
        failed: 'Setup failed',
        disabled: 'Security Hub is disabled',
      }[config.status] || config.status;
      return (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{statusLabel}</Typography>
          {config.status === 'in_progress' && (
            <>
              <Typography sx={{ ...helpTextSx, mb: 2 }}>
                Enrolling member accounts, associating standards, and creating the
                finding aggregator. This can take a few minutes.
              </Typography>
              <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} sx={{ mb: 2 }} />
            </>
          )}
          {config.errorMessage && (
            <Alert severity="warning" sx={{ ...alertSx, mt: 2 }}>{config.errorMessage}</Alert>
          )}
          <Box sx={{ ...insetPanelSx, mt: 2 }}>
            <SummaryRow label="Admin account" value={config.adminAccountId} />
            <SummaryRow label="Home region" value={config.homeRegion} />
          </Box>
        </Box>
      );
    }

    if (active === 0) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ ...helpTextSx, mb: 2 }}>
            Pick one linked account to designate as the Security Hub admin.
            This cannot be changed without tearing down and re-configuring.
          </Typography>
          <Alert severity="warning" sx={{ ...alertSx, mb: 2 }}>
            The admin account is permanent for this deployment. To change it later
            you must disable Security Hub entirely and set it up again —
            workflow-status history will be lost.
          </Alert>
          <Autocomplete
            options={accounts}
            getOptionLabel={(a) => `${a.accountName || a.name || a.accountId} (${a.accountId})`}
            value={accounts.find((a) => a.accountId === adminAccountId) || null}
            onChange={(_, v) => setAdminAccountId(v?.accountId || '')}
            renderInput={(params) => <TextField {...params} label="Admin account" />}
          />
        </Box>
      );
    }

    if (active === 1) {
      return (
        <Box sx={{ py: 1 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Home region</InputLabel>
            <Select
              value={homeRegion}
              label="Home region"
              onChange={(e) => {
                const v = e.target.value;
                setHomeRegion(v);
                if (!linkingRegions.includes(v)) setLinkingRegions([v, ...linkingRegions]);
              }}
            >
              {AWS_REGIONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </FormControl>
          <MultiSelectField
            label="Linking regions"
            options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
            value={linkingRegions}
            onChange={setLinkingRegions}
            lockedValues={homeRegion ? [homeRegion] : []}
          />
        </Box>
      );
    }

    if (active === 2) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ ...helpTextSx, mb: 2 }}>
            Pick at least one standard. These will be enabled uniformly across
            every member account and region.
          </Typography>
          {standards.map((s) => (
            <FormControlLabel
              key={s.arn}
              control={
                <Checkbox
                  checked={selectedStandards.includes(s.arn)}
                  onChange={(e) => {
                    setSelectedStandards((cur) =>
                      e.target.checked ? [...cur, s.arn] : cur.filter((x) => x !== s.arn)
                    );
                  }}
                />
              }
              label={s.displayName}
              sx={{ display: 'block' }}
            />
          ))}
        </Box>
      );
    }

    if (active === 3) {
      const memberOptions = accounts.filter((a) => a.accountId !== adminAccountId);
      return (
        <Box sx={{ py: 1 }}>
          <Typography sx={{ ...helpTextSx, mb: 2 }}>
            Pick the member accounts to enroll. The admin is always included.
          </Typography>
          <MultiSelectField
            label="Member accounts"
            options={memberOptions.map((a) => ({
              value: a.accountId,
              label: `${a.accountName || a.name || a.accountId} (${a.accountId})`,
            }))}
            value={memberAccountIds}
            onChange={setMemberAccountIds}
          />
        </Box>
      );
    }

    // review
    return (
      <Box sx={{ py: 1 }}>
        <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
        <Box sx={insetPanelSx}>
          <SummaryRow label="Admin account" value={adminAccountId} />
          <SummaryRow label="Home region" value={homeRegion} />
          <SummaryRow label="Linking regions" value={linkingRegions.join(', ')} />
          <SummaryRow label="Standards" value={selectedStandards.length} />
          <SummaryRow
            label="Member accounts"
            value={`${memberAccountIds.length + 1} (including admin)`}
          />
        </Box>
        {error && <Alert severity="error" sx={{ ...alertSx, mt: 2 }}>{error}</Alert>}
      </Box>
    );
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<SecurityIcon sx={{ fontSize: 24 }} />}
          title="Set up Security Hub"
          subtitle="Enable AWS Security Hub CSPM across your organization"
        />
      </motion.div>
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          {priorFailure && !config && (
            <Alert severity="warning" sx={{ ...alertSx, mb: 3 }}>
              The previous setup attempt failed: {priorFailure}. Submitting again will clear that state and retry.
            </Alert>
          )}
          <Stepper
            activeStep={active}
            alternativeLabel
            sx={{
              mb: 3,
              overflowX: 'auto',
              '& .MuiStep-root': { minWidth: 88 },
              '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.8rem' },
            }}
          >
            {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>
          {body()}
          {!config && (
            <Box sx={{ ...actionRowSx, justifyContent: 'flex-end', mt: 3 }}>
              <Button disabled={active === 0} onClick={back}>Back</Button>
              {active < steps.length - 1 ? (
                <Button variant="contained" disabled={!canNext()} onClick={next}>Next</Button>
              ) : (
                <Button variant="contained" disabled={submitting} onClick={submit}>
                  {submitting ? 'Enabling…' : 'Enable Security Hub'}
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>

      <ConfigSetupDialog
        open={configCells.length > 0}
        mode="enable"
        cells={configCells}
        onClose={() => { setConfigCells([]); if (onComplete) onComplete(); }}
      />
    </motion.div>
  );
}
